import { useState, useCallback } from "react";
import type { CustomerGeneratedImage } from "@/types/order";

interface ImageGridProps {
  images: CustomerGeneratedImage[];
  selectedIdxs: Set<number>;
  onToggleSelect: (idx: number) => void;
  isRegenerating: boolean;
}

const S3_BASE_URL =
  "https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com";

function resolveImageUrl(image: CustomerGeneratedImage): string {
  if (image.imageUrl) return image.imageUrl;
  if (image.s3Key) return `${S3_BASE_URL}/${image.s3Key}`;
  return "";
}

export default function ImageGrid({
  images,
  selectedIdxs,
  onToggleSelect,
  isRegenerating,
}: ImageGridProps) {
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const openLightbox = useCallback((idx: number) => {
    setLightboxIdx(idx);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
  }, []);

  const goNext = useCallback(() => {
    setLightboxIdx((prev) => (prev !== null ? (prev + 1) % images.length : null));
  }, [images.length]);

  const goPrev = useCallback(() => {
    setLightboxIdx((prev) => (prev !== null ? (prev - 1 + images.length) % images.length : null));
  }, [images.length]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, index) => {
          const isSelected = selectedIdxs.has(img.generatedImageIdx);
          const url = resolveImageUrl(img);

          return (
            <div
              key={img.generatedImageIdx}
              className={`relative group rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                isSelected
                  ? "border-[#C9A96E] shadow-md"
                  : "border-transparent hover:border-gray-200"
              } ${isRegenerating ? "opacity-50 pointer-events-none" : ""}`}
              onClick={() => onToggleSelect(img.generatedImageIdx)}
            >
              {/* Image */}
              <div className="aspect-[4/5] bg-gray-100">
                <img
                  src={url}
                  alt={`생성 이미지 ${img.sortOrder + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Zoom button */}
              <button
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  openLightbox(index);
                }}
              >
                <i className="ri-zoom-in-line text-sm"></i>
              </button>

              {/* Checkbox overlay */}
              <div
                className={`absolute top-2 left-2 w-6 h-6 flex items-center justify-center rounded-md transition-all ${
                  isSelected
                    ? "bg-[#C9A96E] text-white"
                    : "bg-white/80 border border-gray-300 text-transparent group-hover:border-gray-400"
                }`}
              >
                <i className="ri-check-line text-sm"></i>
              </div>

              {/* Regen count badge */}
              <div
                className={`absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  img.canRegenerate
                    ? "bg-white/90 text-gray-600"
                    : "bg-gray-300/90 text-gray-500"
                }`}
              >
                재생성 {img.regenCount}/{img.maxRegenCount}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
            onClick={closeLightbox}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {/* Prev button */}
          {images.length > 1 && (
            <button
              className="absolute left-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
          )}

          {/* Image */}
          <img
            src={resolveImageUrl(images[lightboxIdx])}
            alt={`생성 이미지 ${images[lightboxIdx].sortOrder + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next button */}
          {images.length > 1 && (
            <button
              className="absolute right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          )}

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-sm">
            {lightboxIdx + 1} / {images.length}
          </div>
        </div>
      )}
    </>
  );
}
